import React, { useCallback, useMemo } from 'react'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import ErrorMessage from '../../../../../../components/Form/ErrorMessage'
import CustomLocationsContent from '../../../../../ReusableFormSections/CustomLocationsSection/CustomLocationsContent'
import CountryOrRegionMultiSelect from '../../../../../ReusableFormFields/GeolocationsFields/CountryOrRegionMultiSelect'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { formatGeoTargetingToBE, formatGeoTargetingToFE } from '../../formatters'

import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { campaignSelector } from '../../../../../../modules/selectors/campaigns'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { choicesCountriesSelector } from '../../../../../../modules/selectors/choices'

import geolocationsValidation from './validation'
import { COUNTRY_OR_REGION, CUSTOM_LOCATIONS, GEO_TARGETING } from '../../fields'
import { GEO_EXCLUSIONS, LOCATION_LIST } from '../../../../../ReusableFormFields/GeolocationsFields/fields'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

import useDrawerFormStyles from '../../../../../../styles/common/drawerForms'

const GeoTargetingSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const drawerFormClasses = useDrawerFormStyles()
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const choicesCountries = useSelector(choicesCountriesSelector)
  const { objective: campaignObjective } = useSelector(campaignSelector)

  const { geo_targeting: geoTargeting, geo_exclusions: geoExclusions } = lineItem

  const handleSubmit = useCallback(
    values => {
      const {
        [COUNTRY_OR_REGION]: countryOrRegion,
        [CUSTOM_LOCATIONS]: customLocations,
        [LOCATION_LIST]: customLocationList,
        [GEO_EXCLUSIONS]: geoExclusions
      } = values

      const updateLineItemData = {
        account: selectedAdAccountId,
        campaign_objective: campaignObjective,
        [GEO_TARGETING]: formatGeoTargetingToBE(countryOrRegion),
        [GEO_EXCLUSIONS]: formatGeoTargetingToBE(geoExclusions),
        // customLocations which are populated by BE
        // customLocationList that is ID of new selected list. BE will populate custom locations from selected list
        // add new - LOCATION_LIST: id
        // keep - don't send LOCATION_LIST
        ...(customLocationList && { [LOCATION_LIST]: customLocationList }),
        // delete - LOCATION_LIST: null
        ...(!customLocations.length && !customLocationList && { [LOCATION_LIST]: null }),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateLineItem(updateLineItemData, lineItem.id, FACEBOOK_PLATFORM))
    },
    [dispatch, lineItem.id, campaignObjective, selectedAdAccountId, title]
  )

  const validationSchema = Yup.object(geolocationsValidation)

  const initialValues = useMemo(
    () => ({
      [COUNTRY_OR_REGION]: formatGeoTargetingToFE(geoTargeting, choicesCountries) || [],
      [CUSTOM_LOCATIONS]: (geoTargeting && geoTargeting[CUSTOM_LOCATIONS]) || [],
      [LOCATION_LIST]: '',
      [GEO_EXCLUSIONS]: formatGeoTargetingToFE(geoExclusions, choicesCountries) || []
    }),
    [choicesCountries, geoTargeting, geoExclusions]
  )

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values, errors } = formik

  const { [GEO_TARGETING]: geoTargetingError, [COUNTRY_OR_REGION]: countryError } = errors

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  const hasCountryOrRegion = !!values[COUNTRY_OR_REGION]?.length
  const hasLocationList = !!values[CUSTOM_LOCATIONS]?.length || !!values[LOCATION_LIST]

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <CountryOrRegionMultiSelect isDisabled={hasLocationList} formik={formik} name={COUNTRY_OR_REGION} />
      {countryError && <ErrorMessage error={countryError} />}
      {geoTargetingError && <ErrorMessage error={geoTargetingError} />}
      <div className={drawerFormClasses.subSection}>
        {/*// it's possible to select either location list or add country/regions manually*/}
        <CustomLocationsContent formik={purifiedFormik} isDisabled={hasCountryOrRegion} />
      </div>
      <div className={drawerFormClasses.subSection}>
        <h3 className={drawerFormClasses.sectionTitle}>{t('Exclude locations')}</h3>
        <CountryOrRegionMultiSelect formik={formik} name={GEO_EXCLUSIONS} placeholder="Search..." />
      </div>
    </EditForm>
  )
}

export default GeoTargetingSectionForm
